import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { showErrorToast } from "../toastmsg";

export interface AssigneeOption {
  id: string;
  displayName: string;
}

interface UseAssigneesProps {
  userRole: string | null;
  branchUserIds?: string[];
  enabled?: boolean;
}

export const useAssignees = ({
  userRole,
  branchUserIds = [],
  enabled = true,
}: UseAssigneesProps) => {
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  const [usersMap, setUsersMap] = useState<Map<string, string>>(new Map());
  const [uidToDisplayName, setUidToDisplayName] = useState<Map<string, string>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!enabled) return;

    const canSeeAssignee = [
      "super admin",
      "admin",
      "IT",
      "Branch Manager",
    ].includes(userRole || "");
    if (!canSeeAssignee) {
      setAssigneeOptions([]);
      setUsersMap(new Map());
      setUidToDisplayName(new Map());
      return;
    }

    setIsLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const displayMap = new Map<string, string>();
      const uidMap = new Map<string, string>();
      const filterOptions: AssigneeOption[] = [];

      snap.docs.forEach((doc) => {
        const data = doc.data() as any;
        const displayName = data.displayName;
        const role = data.role;

        if (displayName && doc.id) {
          displayMap.set(doc.id, displayName);
          uidMap.set(displayName.toLowerCase().trim(), doc.id);
        }

        if (!displayName || !doc.id) return;

        if (userRole === "Branch Manager") {
          if (
            branchUserIds.includes(doc.id) &&
            ["sales", "part-time sales", "Branch Manager"].includes(role)
          ) {
            filterOptions.push({
              id: doc.id,
              displayName,
            });
          }
          return;
        }

        if (
          [
            "sales",
            "part-time sales",
            "admin",
            "super admin",
            "Branch Manager",
          ].includes(role)
        ) {
          filterOptions.push({
            id: doc.id,
            displayName,
          });
        }
      });

      setUidToDisplayName(displayMap);
      setUsersMap(uidMap);
      setAssigneeOptions(filterOptions);
    } catch (error) {
      console.error("Failed to fetch users for assignee mapping:", error);
      showErrorToast("Failed to load user list");
    } finally {
      setIsLoading(false);
    }
  }, [userRole, branchUserIds, enabled]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    assigneeOptions,
    usersMap,
    uidToDisplayName,
    isLoading,
    refresh: fetchUsers,
  };
};
